import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
    onAuthStateChanged,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    updateProfile,
    type User,
} from 'firebase/auth';
import { auth, db, rtdb } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ref, onValue, onDisconnect, set, serverTimestamp } from 'firebase/database';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    banError: string | null;
    needsProfileSetup: boolean;
    loginWithGoogle: () => Promise<void>;
    logout: () => Promise<void>;
    updateUserContext: (displayName?: string, photoURL?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}

const googleProvider = new GoogleAuthProvider();

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [banError, setBanError] = useState<string | null>(null);
    const [needsProfileSetup, setNeedsProfileSetup] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

                    // Ban check
                    if (userDoc.exists() && userDoc.data()?.status === 'banned') {
                        await signOut(auth);
                        setUser(null);
                        setNeedsProfileSetup(false);
                        setBanError('Your account has been permanently banned. Contact support if you believe this is an error.');
                        setLoading(false);
                        return;
                    }

                    // Profile existence check — if no Firestore doc, user must complete profile
                    setNeedsProfileSetup(!userDoc.exists());
                } catch (err) {
                    console.error("AuthContext Firestore read failed for userDoc:", err);
                    // If Firestore read fails (e.g., App Check, network error, AdBlock),
                    // we must not blindly let them into the dashboard if they are actually a NEW user.
                    // Estimate if they are newly signed up via Auth metadata:
                    const creationTime = new Date(firebaseUser.metadata.creationTime || 0).getTime();
                    const lastSignInTime = new Date(firebaseUser.metadata.lastSignInTime || 0).getTime();
                    
                    if (Math.abs(lastSignInTime - creationTime) < 10000) {
                        // Within 10 seconds of account creation. Definitely a new user.
                        setNeedsProfileSetup(true);
                    } else {
                        // Older user. Assume profile exists to avoid locking them out during network blips.
                        setNeedsProfileSetup(false);
                    }
                }
                setBanError(null);

                // Presence tracking
                const userStatusRef = ref(rtdb, `/status/${firebaseUser.uid}`);
                const connectedRef = ref(rtdb, '.info/connected');
                onValue(connectedRef, (snap) => {
                    if (snap.val() === true) {
                        onDisconnect(userStatusRef).set({
                            state: 'offline',
                            lastChanged: serverTimestamp(),
                        }).then(() => {
                            set(userStatusRef, {
                                state: 'online',
                                lastChanged: serverTimestamp(),
                            });
                        });
                    }
                });
            } else {
                setNeedsProfileSetup(false);
            }
            setUser(firebaseUser);
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const loginWithGoogle = async () => {
        await signInWithPopup(auth, googleProvider);
    };

    const logout = async () => {
        await signOut(auth);
    };

    const updateUserContext = async (displayName?: string, photoURL?: string) => {
        if (!user) return;
        const updates: any = {};
        if (displayName !== undefined) updates.displayName = displayName;
        if (photoURL !== undefined) updates.photoURL = photoURL;
        await updateProfile(user, updates);
        setUser({ ...user, ...updates } as User);
    };

    return (
        <AuthContext.Provider value={{ user, loading, banError, needsProfileSetup, loginWithGoogle, logout, updateUserContext }}>
            {children}
        </AuthContext.Provider>
    );
}
